import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ignore } from "@/lib/log";

export const THEMES = [
  { key: "soft-mint", dot: "#8FD8B4" },
  { key: "living-green", dot: "#7CC98D" },
  { key: "soft-rose", dot: "#F0A9BE" },
  { key: "lavender", dot: "#BFA9EE" },
  { key: "sky-blue", dot: "#9CC6F0" },
  { key: "classic-grey", dot: "#C7CCD4" },
];

const THEME_KEYS = THEMES.map((t) => t.key);
const STORAGE_KEY = "chronicles.theme";
const DEFAULT_THEME = "soft-mint";

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && THEME_KEYS.includes(saved)) return saved;
    } catch {
      ignore();
    }
    return DEFAULT_THEME;
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Re-apply theme when the cloud sync layer hydrates settings after login.
  useEffect(() => {
    const onSettings = () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved && THEME_KEYS.includes(saved)) setThemeState(saved);
      } catch {
        ignore();
      }
    };
    window.addEventListener("chronicles:settings-changed", onSettings);
    return () => window.removeEventListener("chronicles:settings-changed", onSettings);
  }, []);

  const setTheme = (next) => {
    if (!THEME_KEYS.includes(next)) return;
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
      window.dispatchEvent(new CustomEvent("chronicles:settings-changed"));
    } catch {
      ignore();
    }
  };

  const value = useMemo(() => ({ theme, setTheme, themes: THEMES }), [theme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
